import { ReactElement, useRef, useEffect, ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { Box, BoxProps, Flex, Center } from "@chakra-ui/react";
import {
  IoSettingsSharp,
  IoHomeSharp,
  IoMic,
  IoNotificationsSharp,
} from "react-icons/io5";
import { useAtom } from "jotai";
import {
  contractEnginesAtom,
  recTypeAtom,
  footerIndexAtom,
} from "../../../store";
import { ANALYSIS_ENGINE } from "../../../environments";
import { getSelectableEngineList } from "../../../pages/engines";
import { GraphIcon } from "../../../assets/Icons";
import { useDefaultPath } from "../../../hooks/useDefaultPath";

interface FooterItem {
  id: number;
  label: string;
  icon: ComponentType;
  isRecordButton?: boolean;
  boxProps?: BoxProps;
}

interface FooterButtonProps extends BoxProps {
  icon: ComponentType;
  isActive: boolean;
}

function RegularFooterButton({
  icon: Icon,
  isActive,
  onClick,
  ...boxProps
}: Readonly<FooterButtonProps>): ReactElement {
  return (
    <Box
      fontWeight={600}
      color="primary.theme_lv1"
      opacity={isActive ? 1 : 0.5}
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      p={3}
      {...boxProps}
    >
      <Icon />
    </Box>
  );
}

interface RecordFooterButtonProps extends BoxProps {
  icon: ComponentType;
}

function RecordFooterButton({
  icon: Icon,
  onClick,
  ...boxProps
}: Readonly<RecordFooterButtonProps>): ReactElement {
  return (
    <Box
      fontWeight={600}
      color="white"
      bg="primary.theme_lv1"
      borderRadius="50%"
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      p={3}
      {...boxProps}
    >
      <Icon />
    </Box>
  );
}

/*
 * ログイン後のフッター
 */
export function Footer(): ReactElement {
  const [, setRecType] = useAtom(recTypeAtom);

  const [contractEngines] = useAtom(contractEnginesAtom);

  const [footerIndex, setFooterIndex] = useAtom(footerIndexAtom);
  const innerFooterIndex = useRef(0);

  const navigate = useNavigate();
  const defaultPath = useDefaultPath();

  const isFooterclick = useRef(false);

  const footerItems: FooterItem[] = [
    {
      id: 0,
      label: "home",
      icon: IoHomeSharp,
      boxProps: {
        fontSize: "2xl",
      },
    },
    {
      id: 1,
      label: "analysis-result",
      icon: GraphIcon,
      boxProps: {
        fontSize: "2xl",
      },
    },
    {
      id: 2,
      label: "record",
      icon: IoMic,
      isRecordButton: true,
      boxProps: {
        fontSize: "2xl",
      },
    },
    {
      id: 4,
      label: "notifications",
      icon: IoNotificationsSharp,
      boxProps: {
        fontSize: "2xl",
      },
    },
    {
      id: 3,
      label: "setting",
      icon: IoSettingsSharp,
      boxProps: {
        fontSize: "2xl",
      },
    },
  ];

  const handleClick = (index: number): void => {
    setFooterIndex(index);
    innerFooterIndex.current = index;
    isFooterclick.current = true;
  };

  useEffect(() => {
    if (!isFooterclick.current) return;

    isFooterclick.current = false;

    const getAnalyzeUrl = (): string => {
      if (ANALYSIS_ENGINE) {
        return `../engines/${ANALYSIS_ENGINE}/recording`;
      }

      const url_list = getSelectableEngineList(contractEngines);
      if (url_list.length == 1) {
        return "../engines/" + url_list[0] + "/recording";
      }

      return "../engines";
    };

    const timer = setTimeout(() => {
      switch (innerFooterIndex.current) {
        case 0:
          navigate(defaultPath);
          break;

        case 1:
          navigate("../analysis-result");
          break;

        case 2:
          setRecType(0);
          navigate(getAnalyzeUrl());
          setFooterIndex(0);
          break;

        case 3:
          navigate("../setting");
          break;

        case 4:
          navigate("../notifications");
          break;
      }
    }, 10);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerIndex]);

  return (
    <Flex w="full" bg="common.base">
      <Center
        w="full"
        mx={{ base: 0, md: "12%" }}
        py={4}
        justifyContent="space-evenly"
      >
        {footerItems.map((footerItem) => {
          const isActive = footerIndex === footerItem.id;

          return (
            <Box key={footerItem.id} data-testid={`footer-${footerItem.label}`}>
              {footerItem.isRecordButton ? (
                <RecordFooterButton
                  icon={footerItem.icon}
                  onClick={() => handleClick(footerItem.id)}
                  {...footerItem.boxProps}
                />
              ) : (
                <RegularFooterButton
                  icon={footerItem.icon}
                  isActive={isActive}
                  onClick={() => handleClick(footerItem.id)}
                  {...footerItem.boxProps}
                />
              )}
            </Box>
          );
        })}
      </Center>
    </Flex>
  );
}
